import { useEffect, useState } from 'react';
import { debounce } from 'lodash';

import { IconArrowDown } from 'components/Icons/IconArrowDown';
import { useAuthentication } from 'hooks/authentication';
import { useConversation } from 'hooks/conversation';
import { useAnalytics } from 'hooks/useAnalytics';
import { useView } from 'hooks/view';
import { DataDogLogger } from 'utils/dataDog';
import { htmlId } from 'utils/html';
import { scrollToMessagesListBottom } from 'utils/message';

import { TIME_BUFFER_SCROLL_INIT } from 'routes/Chat/Messages/ScrollingObservers/config';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import { ACTIONS } from 'constants/hook';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { PARTICIPANT_PREFIX } from 'constants/twilio';
import { ANALYTICS } from 'analytics/constants';
import { CHAT_UI_PREFIX } from 'constants/id';
import { BOUNCE_ANIMATION, StyledBottomCTAWrapper } from './ScrollToBottomCTA.style';
import { AnalyticClickedDetailsType } from 'analytics/types';

export const ScrollToBottomCTA = () => {
  const { authenticationState } = useAuthentication();
  const { viewState, dispatchViewAction } = useView();
  const { conversationState } = useConversation();
  const { trackEvent } = useAnalytics();

  const [unixTimeMsgLastClickedCTA, setUnixTimeMsgLastClickedCTA] = useState<number | undefined>(
    undefined,
  );
  const [isAnimateCTA, setIsAnimateCTA] = useState(false);

  const { isAutoScrollNextMsg, isCTAVisible, isLastMsgInView, unixTimeLastClickedCTA } =
    viewState.scrollToBottom;

  /**
   * When CTA is visible (through manually scrolling conversation thread),
   * animate the button to indicate that a new message has arrived.
   */
  const onHandleNewMessageAnimation = async () => {
    if (isCTAVisible && !isLastMsgInView && !isAutoScrollNextMsg) {
      const messages = await window[CHATUI_WINDOW_PATH].getMessages();
      const lastMsg = messages[messages.length - 1];
      const isLastMsgByAdvocate = lastMsg?.author.includes(PARTICIPANT_PREFIX.ADVOCATE) ?? false;
      if (isLastMsgByAdvocate) {
        setIsAnimateCTA(true);
        setTimeout(() => {
          setIsAnimateCTA(false);
        }, BOUNCE_ANIMATION.COUNT * BOUNCE_ANIMATION.DURATION);
      }
    }
  };

  const onClickScrollToBottomCTA = debounce(() => {
    DataDogLogger.action(CUSTOM_ACTION_NAMES.SCROLL_CONVO_BOTTOM_CTA_CLICK);
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.CLICKED,
      details: {
        analytic_click_id: `${CHAT_UI_PREFIX}-scroll-arrow-button`,
      } as AnalyticClickedDetailsType,
    });

    setUnixTimeMsgLastClickedCTA(conversationState.unixTimeLastMsgReceived);
    scrollToMessagesListBottom();
    if (isCTAVisible) {
      const nextViewState = { ...viewState };
      nextViewState.scrollToBottom.unixTimeLastClickedCTA = Date.now();
      nextViewState.scrollToBottom.isCTAVisible = false;
      nextViewState.scrollToBottom.isAutoScrollNextMsg = true;
      dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
    }
  }, 250);

  useEffect(() => {
    const lastInit = authenticationState.unixTimeLastInitialization;
    const isJustInit = lastInit && Date.now() < lastInit + TIME_BUFFER_SCROLL_INIT + 2500;
    if (!isJustInit) onHandleNewMessageAnimation();
  }, [conversationState.messages, isCTAVisible]);

  useEffect(() => {
    if (isCTAVisible && !seen) {
      DataDogLogger.action(CUSTOM_ACTION_NAMES.SCROLL_CONVO_BOTTOM_CTA_SHOWN);
      trackEvent({
        analyticEvent: ANALYTICS.EVENT.SCROLL_ARROW_SHOWN,
      });
    }
  }, [isCTAVisible]);

  if (!isCTAVisible) return null;

  // Should not be possible to see CTA more than once per new message
  const seen = unixTimeMsgLastClickedCTA === conversationState.unixTimeLastMsgReceived;
  if (unixTimeLastClickedCTA && seen) {
    return null;
  }

  return (
    <StyledBottomCTAWrapper animate={isAnimateCTA} id={htmlId('bottom-cta-wrapper')}>
      <IconArrowDown onClick={onClickScrollToBottomCTA} />
    </StyledBottomCTAWrapper>
  );
};
